/**
 * LR Direct — Printable Dispatch Sheet
 *
 * Auth: shipper-owner, awarded broker, or admin (enforced by get_direct_load_dispatch RPC).
 * If `is_unlocked === false`, renders an unauthorized state.
 *
 * Route: /lr-direct/dispatch/:loadId
 *
 * Layout is delegated to <DispatchSheet />, which is also used by the broker's
 * dispatch page so the on-screen view matches the printed sheet.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Lock, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";
import { DispatchSheet } from "@/components/lr-direct/DispatchSheet";

interface DispatchStop {
  id: string;
  stop_number: number;
  stop_type: string;
  name: string | null;
  address: string | null;
  city: string;
  state: string;
  postal_code: string | null;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  instructions: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  pickup_number: string | null;
  pallet_count: number | null;
  confidential_notes: string | null;
  is_unlocked: boolean;
}

interface DirectLoad {
  id: string;
  load_number: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  pickup_date: string;
  delivery_date: string | null;
  commodity: string | null;
  weight_lbs: number | null;
  length_ft: number | null;
  total_miles: number | null;
  special_instructions: string | null;
  shipper_notes_to_brokers: string | null;
  status: string;
  awarded_at: string | null;
  awarded_bid_id: string | null;
  created_by: string;
}

interface BrokerContact {
  company_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
}

// Layout formatting moved into <DispatchSheet />.

export default function LRDirectDispatchSheetPage() {
  usePageMeta({
    title: "Dispatch Sheet | LaneRunner LR Direct",
    description: "Printable dispatch sheet for awarded LR Direct load.",
  });

  const { loadId } = useParams<{ loadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [load, setLoad] = useState<DirectLoad | null>(null);
  const [stops, setStops] = useState<DispatchStop[]>([]);
  const [broker, setBroker] = useState<BrokerContact | null>(null);
  const [awardedAmount, setAwardedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    if (!loadId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);

      const [loadRes, stopsRes, authRes] = await Promise.all([
        supabase.from("direct_loads" as any).select("*").eq("id", loadId).maybeSingle(),
        supabase.rpc("get_direct_load_dispatch" as any, { p_load_id: loadId }),
        supabase.rpc("get_dispatch_for_user" as any, { p_load_id: loadId }),
      ]);

      if (cancelled) return;

      const ld = loadRes.data as unknown as DirectLoad | null;
      const stopRows = (stopsRes.data ?? []) as unknown as DispatchStop[];
      const auth = (authRes.data as any) ?? {};

      if (!ld) {
        setLoading(false);
        return;
      }

      // Authorization: trust get_dispatch_for_user's `unlocked` flag (shipper-owner, awarded broker, or admin).
      // Decoupled from stop data — some loads have empty direct_load_stops but valid dispatch stops.
      const unlockedFromAuth = auth?.unlocked === true || auth?.is_owner === true;
      const unlockedFromStops = stopRows.length > 0 && stopRows[0].is_unlocked;
      const unlocked = unlockedFromAuth || unlockedFromStops;

      if (!unlocked) {
        setUnauthorized(true);
        setLoad(ld);
        setLoading(false);
        return;
      }

      // Fall back to dispatch stops when direct_load_stops is empty
      let finalStops = stopRows;
      if (finalStops.length === 0 && Array.isArray(auth?.stops) && auth.stops.length > 0) {
        finalStops = auth.stops.map((s: any) => ({
          id: s.id ?? `${s.stop_type}-${s.stop_number}`,
          stop_number: s.stop_number,
          stop_type: s.stop_type,
          name: s.name ?? null,
          address: s.address ?? null,
          city: s.city ?? "",
          state: s.state ?? "",
          postal_code: s.postal_code ?? null,
          scheduled_date: s.scheduled_date ?? "",
          scheduled_time_start: s.scheduled_time_start ?? null,
          scheduled_time_end: s.scheduled_time_end ?? null,
          instructions: s.instructions ?? null,
          contact_name: s.contact_name ?? null,
          contact_phone: s.contact_phone ?? null,
          contact_email: s.contact_email ?? null,
          pickup_number: s.reference_number ?? s.pickup_number ?? null,
          pallet_count: s.pallet_count ?? null,
          confidential_notes: s.confidential_notes ?? null,
          is_unlocked: true,
        })) as DispatchStop[];
      }

      setLoad(ld);
      setStops(finalStops);

      // Fetch awarded broker contact + bid amount (shipper-owner only sees this via RPC)
      if (ld.awarded_bid_id) {
        const [contactRes, bidRes] = await Promise.all([
          supabase.rpc("get_direct_award_broker_contact" as any, { p_load_id: loadId }),
          supabase
            .from("direct_bids" as any)
            .select("bid_amount")
            .eq("id", ld.awarded_bid_id)
            .maybeSingle(),
        ]);
        if (!cancelled) {
          const c = (contactRes.data as any[])?.[0];
          if (c) setBroker(c as BrokerContact);
          const amt = (bidRes.data as any)?.bid_amount;
          if (amt != null) setAwardedAmount(Number(amt));
        }
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadId]);

  // Auto-print once data loads (one-shot)
  useEffect(() => {
    if (!loading && load && stops.length > 0 && !printed && !unauthorized) {
      const t = setTimeout(() => {
        setPrinted(true);
        window.print();
      }, 600);
      return () => clearTimeout(t);
    }
  }, [loading, load, stops.length, printed, unauthorized]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
      </div>
    );
  }

  if (unauthorized || !load) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background p-6">
        <Lock className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-muted-foreground font-medium">
          {!load ? "Dispatch sheet not found." : "You don't have access to this dispatch sheet."}
        </p>
        <p className="text-xs text-muted-foreground max-w-md text-center">
          Only the shipper who posted this load or the awarded broker can view the dispatch sheet.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  // Stops are passed straight to <DispatchSheet />, which renders them in true
  // route sequence (stop_number order, pickups/deliveries interleaved).

  const ratePerMile =
    awardedAmount != null && load.total_miles ? awardedAmount / Number(load.total_miles) : null;

  return (
    <div className="min-h-screen bg-white text-black print:bg-white">
      {/* Toolbar — hidden when printing */}
      <div className="print:hidden border-b border-gray-200 bg-gray-50 px-3 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-10 gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-black hover:bg-gray-200">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => window.print()}
            size="sm"
            variant="outline"
            className="text-black border-gray-300 hover:bg-gray-200"
            title="Choose 'Save as PDF' in the print dialog"
          >
            <Download className="h-4 w-4" /> Download PDF
          </Button>
          <Button
            onClick={() => window.print()}
            size="sm"
            style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            className="text-white"
          >
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {/* Printable sheet — same layout used on the broker's dispatch page */}
      <DispatchSheet
        load={load}
        stops={stops}
        broker={broker}
        awardedAmount={awardedAmount}
        totalMiles={load.total_miles}
        className="print:p-6"
      />

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { margin: 0.5in; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}

